import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Edit, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import ProductForm from "./ProductForm";
import AdminCsvImport from "./AdminCsvImport";

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?} for edit, {} for new, null for none
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products");
      setItems(r.data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (editing !== null) {
    return <ProductForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl">Products</h1>
        <div className="flex gap-3">
          <button onClick={() => setShowImport(!showImport)} className="btn-outline text-sm" data-testid="toggle-csv-import">
            <Upload size={14} className="inline mr-2 -mt-0.5" /> {showImport ? "Hide Import" : "CSV Import"}
          </button>
          <button onClick={() => setEditing({})} className="btn-primary" data-testid="add-product-btn">
            <Plus size={16} className="inline mr-2 -mt-0.5" /> New Product
          </button>
        </div>
      </div>

      {showImport && (
        <div className="mb-6">
          <AdminCsvImport onDone={() => { load(); setShowImport(false); }} />
        </div>
      )}

      {loading && <div className="text-zinc-500">Loading…</div>}
      {!loading && items.length === 0 && <div className="text-zinc-500">No products yet.</div>}

      <div className="space-y-2" data-testid="admin-products-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 border border-white/10 rounded p-3 bg-[var(--cl-surface)]">
            <img src={p.images?.[0] || "https://images.unsplash.com/photo-1599873265732-4511510f0908"}
                 className="w-16 h-20 object-cover rounded" alt="" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{p.title}</div>
              <div className="text-xs text-zinc-500 mt-1">
                {p.category} · {p.sport}
                {p.serial_number && ` · ${p.serial_number}`}
                {p.stock !== undefined && ` · stock: ${p.stock}`}
              </div>
            </div>
            <div className="font-display text-2xl">${p.price?.toLocaleString()}</div>
            <button onClick={() => setEditing(p)} className="p-2 hover:text-[var(--cl-orange)]" data-testid={`edit-${p.id}`}><Edit size={16} /></button>
            <button onClick={() => del(p.id)} className="p-2 hover:text-[var(--cl-primary)]" data-testid={`delete-${p.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
